import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";

const PLAN_COLORS = { pro: "hsl(35 55% 45%)", keepsake: "hsl(210 55% 45%)", legacy: "hsl(280 40% 45%)", free: "hsl(0 0% 55%)" };

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: "100" });
        if (search) params.set("search", search);
        if (plan) params.set("plan", plan);
        if (status) params.set("status", status);
        const r = await api.get(`/admin/users?${params.toString()}`);
        setRows(r.data?.users || []); setTotal(r.data?.total || 0);
      } catch {} finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, plan, status]);

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-users">
      <h1 className="font-display text-2xl font-semibold">Users</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">{total} total</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input data-testid="admin-users-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="w-full h-10 pl-9 pr-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} className="h-10 px-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm">
          <option value="">All plans</option><option value="free">Free</option><option value="keepsake">Keepsake</option><option value="legacy">Legacy</option><option value="pro">Pro</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm">
          <option value="">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="px-4 py-2 font-semibold">Name / email</th>
              <th className="px-4 py-2 font-semibold">Plan</th>
              <th className="px-4 py-2 font-semibold text-right">Events</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">No users match.</td></tr>
            : rows.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50">
                <td className="px-4 py-2.5"><Link to={`/admin/users/${u.user_id}`} className="font-semibold hover:underline">{u.name || u.email}</Link><div className="text-[11px] text-[hsl(var(--muted-foreground))]">{u.email}</div></td>
                <td className="px-4 py-2.5"><span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: `${PLAN_COLORS[u.plan] || PLAN_COLORS.free}22`, color: PLAN_COLORS[u.plan] || PLAN_COLORS.free }}>{u.plan || "free"}</span></td>
                <td className="px-4 py-2.5 text-right tabular-nums">{u.events_count || 0}</td>
                <td className="px-4 py-2.5">{u.suspended ? <span className="text-[hsl(var(--destructive))] font-semibold text-xs">Suspended</span> : <span className="text-xs text-[hsl(var(--muted-foreground))]">Active</span>}</td>
                <td className="px-4 py-2.5 text-xs text-[hsl(var(--muted-foreground))]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
